import { uuid } from '../core/util';
import type { Difficulty, Side } from '../data/types';
import type { Match, PlayerStats, TelemetryEvent } from '../sim/match';

export const APP_VERSION = '0.1.0';

const KEY_MATCHES = 'peasents.matches.v1';
const KEY_PLAYER = 'peasents.playerId';
const KEY_OUTBOX = 'peasents.outbox.v1';
const KEY_SETTINGS = 'peasents.settings.v1';
const MAX_STORED = 300;

export interface Settings {
  sound: boolean;
  music: boolean;
  tutorialDone: boolean;
  nickname: string;
  telemetryUrl: string;
  seed: string;
  matchesPlayed: number;
  /** Optional override so a rebuild is not required to point at a Supabase project. */
  supabaseUrl: string;
  supabaseAnonKey: string;
  /** Photon Realtime App Id. Public client id, same idea as a Supabase anon key. */
  photonAppId: string;
}

export const DEFAULT_SETTINGS: Settings = {
  sound: true,
  music: true,
  tutorialDone: false,
  nickname: '',
  telemetryUrl: '',
  seed: '',
  matchesPlayed: 0,
  supabaseUrl: '',
  supabaseAnonKey: '',
  photonAppId: '',
};

export interface SideSummary extends Omit<PlayerStats, 'firstSwitchDelays'> {
  hearts: number;
  shop: Record<string, number>;
  commander: string;
  avgTimeToFirstSwitch: number | null;
  towersEverSwitchedPct: number;
  familiesSummoned: Record<string, number>;
  finalBoard: Array<{ tile: number; family: string; rank: number; lane: number }>;
  deck: string[];
  bosses: string[];
}

export interface MatchSummary {
  result: 'win' | 'loss' | 'draw';
  reason: string;
  durationSec: number;
  overtime: boolean;
  player: SideSummary;
  opponent: SideSummary;
  laneSwitchesPerMinute: number;
  leakSourcesByLane: Record<string, number>;
}

export interface MatchRecord {
  id: string;
  version: string;
  playerId: string;
  nickname: string;
  seed: number;
  difficulty: Difficulty;
  startedAt: string;
  endedAt: string;
  userAgent: string;
  viewport: { w: number; h: number };
  mode?: 'bot' | 'online';
  summary: MatchSummary;
  events: TelemetryEvent[];
  feedback?: { stars: number; text: string; wouldPlayAgain: boolean | null };
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or unavailable */
  }
}

export function loadSettings(): Settings {
  return { ...DEFAULT_SETTINGS, ...read<Partial<Settings>>(KEY_SETTINGS, {}) };
}

export function saveSettings(s: Settings): void {
  write(KEY_SETTINGS, s);
}

export function getPlayerId(): string {
  let id = localStorage.getItem(KEY_PLAYER);
  if (!id) {
    id = uuid();
    localStorage.setItem(KEY_PLAYER, id);
  }
  return id;
}

export function loadMatches(): MatchRecord[] {
  return read<MatchRecord[]>(KEY_MATCHES, []);
}

export function clearMatches(): void {
  localStorage.removeItem(KEY_MATCHES);
  localStorage.removeItem(KEY_OUTBOX);
}

/**
 * Collects the raw event stream of a match and turns it into a MatchRecord
 * with the lane-centric summary metrics the design spec asks for.
 */
export class MatchTelemetry {
  readonly events: TelemetryEvent[] = [];
  readonly startedAt = new Date();
  private familiesSummoned: [Record<string, number>, Record<string, number>] = [{}, {}];
  private leakSourcesByLane: Record<string, number> = {};

  constructor(
    private readonly match: Match,
    private readonly difficulty: Difficulty,
    private readonly localSide: Side = 0,
    private readonly mode: 'bot' | 'online' = 'bot',
  ) {
    match.telemetry = (e) => this.record(e);
    this.record({ t: 0, type: 'match_start', seed: match.config.seed, difficulty, commander: match.config.playerCommander, aiCommander: match.config.aiCommander });
  }

  record(e: TelemetryEvent): void {
    this.events.push({ ...e, t: Math.round(e.t * 100) / 100 });
    if (e.type === 'summon' && typeof e.side === 'number') {
      const fam = String(e.family);
      this.familiesSummoned[e.side][fam] = (this.familiesSummoned[e.side][fam] ?? 0) + 1;
    }
    if (e.type === 'leak' && e.side === this.localSide) {
      const key = `${e.lane === 0 ? 'left' : 'right'}:${String(e.source)}`;
      this.leakSourcesByLane[key] = (this.leakSourcesByLane[key] ?? 0) + Number(e.amount ?? 1);
    }
  }

  /** Records a tutorial / UI event (not part of the sim). */
  ui(type: string, data: Record<string, unknown> = {}): void {
    this.record({ t: this.match.time, type: `ui_${type}`, ...data });
  }

  private sideSummary(side: Side): SideSummary {
    const p = this.match.players[side];
    const { firstSwitchDelays, ...rest } = p.stats;
    const towers = this.match.towersOf(side);
    const switched = towers.filter((t) => t.everSwitched).length;
    return {
      ...rest,
      hearts: p.hearts,
      shop: { ...p.shop },
      commander: p.commanderId,
      avgTimeToFirstSwitch: firstSwitchDelays.length
        ? Math.round((firstSwitchDelays.reduce((a, b) => a + b, 0) / firstSwitchDelays.length) * 10) / 10
        : null,
      towersEverSwitchedPct: towers.length ? Math.round((switched / towers.length) * 100) : 0,
      familiesSummoned: { ...this.familiesSummoned[side] },
      finalBoard: towers.map((t) => ({ tile: t.tile, family: t.familyId, rank: t.rank, lane: t.lane })),
      deck: [...p.deck],
      bosses: [...p.bosses],
      essenceEarned: Math.round(p.stats.essenceEarned),
      essenceSpent: Math.round(p.stats.essenceSpent),
      damageDealt: Math.round(p.stats.damageDealt),
      bossDamage: Math.round(p.stats.bossDamage),
    };
  }

  finish(): MatchRecord {
    const m = this.match;
    const you = this.localSide;
    const them = (1 - you) as Side;
    const result: MatchSummary['result'] = m.winner === you ? 'win' : m.winner === them ? 'loss' : 'draw';
    let reason = m.endReason;
    if (you === 1) {
      if (reason === 'Your fortress fell') reason = 'Enemy fortress destroyed';
      else if (reason === 'Enemy fortress destroyed') reason = 'Your fortress fell';
    }
    this.record({ t: m.time, type: 'match_end', result, reason, hearts: [m.players[you].hearts, m.players[them].hearts] });
    const player = this.sideSummary(you);
    const summary: MatchSummary = {
      result,
      reason,
      durationSec: Math.round(m.time),
      overtime: m.overtime,
      player,
      opponent: this.sideSummary(them),
      laneSwitchesPerMinute: m.time > 0 ? Math.round((player.laneSwitches / (m.time / 60)) * 10) / 10 : 0,
      leakSourcesByLane: { ...this.leakSourcesByLane },
    };
    const settings = loadSettings();
    return {
      id: uuid(),
      version: APP_VERSION,
      playerId: getPlayerId(),
      nickname: settings.nickname,
      seed: m.config.seed,
      difficulty: this.difficulty,
      startedAt: this.startedAt.toISOString(),
      endedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      mode: this.mode,
      summary,
      events: this.events,
    };
  }
}

export function storeMatch(record: MatchRecord): void {
  const all = loadMatches();
  const idx = all.findIndex((r) => r.id === record.id);
  if (idx >= 0) all[idx] = record;
  else all.push(record);
  while (all.length > MAX_STORED) all.shift();
  write(KEY_MATCHES, all);
}

export function updateMatchFeedback(id: string, feedback: MatchRecord['feedback']): MatchRecord | null {
  const all = loadMatches();
  const rec = all.find((r) => r.id === id);
  if (!rec) return null;
  rec.feedback = feedback;
  write(KEY_MATCHES, all);
  return rec;
}

/** Endpoint priority: user setting > build-time env var. */
export function telemetryEndpoint(): string {
  const s = loadSettings();
  return s.telemetryUrl || (import.meta.env.VITE_TELEMETRY_URL as string | undefined) || '';
}

export async function uploadMatch(record: MatchRecord): Promise<boolean> {
  const url = telemetryEndpoint();
  if (!url) return false;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(record),
      keepalive: true,
    });
    if (!res.ok) throw new Error(String(res.status));
    return true;
  } catch {
    const outbox = read<string[]>(KEY_OUTBOX, []);
    if (!outbox.includes(record.id)) outbox.push(record.id);
    write(KEY_OUTBOX, outbox.slice(-100));
    return false;
  }
}

/** Retries anything that failed to upload earlier. */
export async function flushOutbox(): Promise<void> {
  const url = telemetryEndpoint();
  if (!url) return;
  const outbox = read<string[]>(KEY_OUTBOX, []);
  if (outbox.length === 0) return;
  const all = loadMatches();
  const remaining: string[] = [];
  for (const id of outbox) {
    const rec = all.find((r) => r.id === id);
    if (!rec) continue;
    try {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rec) });
      if (!res.ok) remaining.push(id);
    } catch {
      remaining.push(id);
    }
  }
  write(KEY_OUTBOX, remaining);
}

export function exportAllMatches(): void {
  const data = { exportedAt: new Date().toISOString(), version: APP_VERSION, playerId: getPlayerId(), matches: loadMatches() };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `peasents-telemetry-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export interface Aggregate {
  matches: number;
  wins: number;
  losses: number;
  draws: number;
  avgDuration: number;
  avgLaneSwitches: number;
  avgTimeToFirstSwitch: number | null;
  avgMerges: number;
  avgSummons: number;
  leaksTaken: [number, number];
  leaksDealt: [number, number];
  byCommander: Record<string, { matches: number; wins: number }>;
  byDifficulty: Record<string, { matches: number; wins: number }>;
  familyUsage: Record<string, number>;
  avgStars: number | null;
  wouldPlayAgainPct: number | null;
}

export function aggregate(records: MatchRecord[]): Aggregate {
  const agg: Aggregate = {
    matches: records.length,
    wins: 0,
    losses: 0,
    draws: 0,
    avgDuration: 0,
    avgLaneSwitches: 0,
    avgTimeToFirstSwitch: null,
    avgMerges: 0,
    avgSummons: 0,
    leaksTaken: [0, 0],
    leaksDealt: [0, 0],
    byCommander: {},
    byDifficulty: {},
    familyUsage: {},
    avgStars: null,
    wouldPlayAgainPct: null,
  };
  if (records.length === 0) return agg;
  let ttfsSum = 0;
  let ttfsN = 0;
  let stars = 0;
  let starsN = 0;
  let again = 0;
  let againN = 0;
  for (const r of records) {
    const s = r.summary;
    if (s.result === 'win') agg.wins++;
    else if (s.result === 'loss') agg.losses++;
    else agg.draws++;
    agg.avgDuration += s.durationSec;
    agg.avgLaneSwitches += s.player.laneSwitches;
    agg.avgMerges += s.player.merges;
    agg.avgSummons += s.player.summons;
    agg.leaksTaken[0] += s.player.leaksTaken[0];
    agg.leaksTaken[1] += s.player.leaksTaken[1];
    agg.leaksDealt[0] += s.player.leaksDealt[0];
    agg.leaksDealt[1] += s.player.leaksDealt[1];
    if (s.player.avgTimeToFirstSwitch !== null) {
      ttfsSum += s.player.avgTimeToFirstSwitch;
      ttfsN++;
    }
    const c = (agg.byCommander[s.player.commander] ??= { matches: 0, wins: 0 });
    c.matches++;
    if (s.result === 'win') c.wins++;
    const d = (agg.byDifficulty[r.difficulty] ??= { matches: 0, wins: 0 });
    d.matches++;
    if (s.result === 'win') d.wins++;
    for (const [fam, n] of Object.entries(s.player.familiesSummoned)) agg.familyUsage[fam] = (agg.familyUsage[fam] ?? 0) + n;
    if (r.feedback) {
      if (r.feedback.stars > 0) {
        stars += r.feedback.stars;
        starsN++;
      }
      if (r.feedback.wouldPlayAgain !== null) {
        againN++;
        if (r.feedback.wouldPlayAgain) again++;
      }
    }
  }
  const n = records.length;
  agg.avgDuration = Math.round(agg.avgDuration / n);
  agg.avgLaneSwitches = Math.round((agg.avgLaneSwitches / n) * 10) / 10;
  agg.avgMerges = Math.round((agg.avgMerges / n) * 10) / 10;
  agg.avgSummons = Math.round((agg.avgSummons / n) * 10) / 10;
  agg.avgTimeToFirstSwitch = ttfsN ? Math.round((ttfsSum / ttfsN) * 10) / 10 : null;
  agg.avgStars = starsN ? Math.round((stars / starsN) * 10) / 10 : null;
  agg.wouldPlayAgainPct = againN ? Math.round((again / againN) * 100) : null;
  return agg;
}
